/**
 * Hooks for the team-level billing UI (M70).
 *
 *   useTeamInvoices(teamId, page) — list of billing periods + spend totals
 *   useSubscriptionPeriodUsage(subscriptionId, periodId) — per-alias breakdown
 *
 * Both use TanStack Query with the standard 60s stale time + skip-on-auth
 * retry policy mirrored from useOrganisationUsage.
 */
import { useQuery } from "@tanstack/react-query";
import {
  listTeamInvoices,
  listPayments,
  getSubscriptionPeriodUsage,
  getMySubscription,
  type TeamInvoicesResponse,
  type PaymentsResponse,
  type PeriodUsageResponse,
  type MySubscription,
} from "../services/billingApi";
import { getResponseStatus } from "../utils/errorUtils";

const PAGE_SIZE = 20;
const STALE_MS = 60_000;

/**
 * Base query key for the payments list. The live-refresh consumer
 * (useUnifiedEventConsumer) invalidates by this prefix, so a top-up mint or a
 * subscription change refreshes every mounted payments query.
 */
export const PAYMENTS_QUERY_KEY = ["payments"] as const;

/**
 * Lists the actual card payments (top-ups + subscription charges) for one
 * billing account, newest-first. Disabled until a `subscriptionId` is resolved.
 * Mirrors {@link useTeamInvoices}: 60s stale time + skip-on-auth retry policy.
 */
export function usePayments(subscriptionId: string | undefined, page = 0) {
  return useQuery<PaymentsResponse>({
    queryKey: [...PAYMENTS_QUERY_KEY, subscriptionId, page],
    queryFn: () =>
      listPayments(subscriptionId!, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    enabled: Boolean(subscriptionId),
    staleTime: STALE_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });
}

export function useTeamInvoices(teamId: string | undefined, page = 0) {
  return useQuery<TeamInvoicesResponse>({
    queryKey: ["team-invoices", teamId, page],
    queryFn: () =>
      listTeamInvoices(teamId!, {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    enabled: Boolean(teamId),
    staleTime: STALE_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });
}

/**
 * Resolves the authenticated user's default subscription. Returns null
 * (not an error) when the user has no team with an open subscription.
 */
export function useMySubscription() {
  return useQuery<MySubscription | null>({
    queryKey: ["my-subscription"],
    queryFn: getMySubscription,
    staleTime: STALE_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });
}

export function useSubscriptionPeriodUsage(
  subscriptionId: string | undefined,
  periodId: string | undefined,
) {
  return useQuery<PeriodUsageResponse>({
    queryKey: ["subscription-period-usage", subscriptionId, periodId],
    queryFn: () => getSubscriptionPeriodUsage(subscriptionId!, periodId!),
    enabled: Boolean(subscriptionId && periodId),
    staleTime: STALE_MS,
    retry: (failureCount, error: unknown) => {
      const status = getResponseStatus(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
  });
}
